import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';

const JobPosting = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [jobData, setJobData] = useState({
    title: '',
    description: '',
    requiredTrade: '',
    location: '',
    budget: '',
    budgetType: 'negotiable',
    urgency: 'normal',
    images: []
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dcTech_token') || localStorage.getItem('token');
      
      const response = await fetch(`${backendUrl}/api/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCategories(data.services || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setJobData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    // For now, we'll just store the file names
    // In a real implementation, you'd upload to a storage service
    const imageUrls = files.map(file => URL.createObjectURL(file));
    setJobData(prev => ({
      ...prev,
      images: [...prev.images, ...imageUrls]
    }));
  };

  const removeImage = (index) => {
    setJobData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('dcTech_token') || localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/jobs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...jobData,
          budget: jobData.budget ? parseFloat(jobData.budget) : null
        })
      });

      if (response.ok) {
        toast({
          title: "Job Posted Successfully! 🎉",
          description: "Your job has been posted and providers can now bid on it.",
          className: "bg-green-50 border-green-200 text-green-800"
        });

        // Reset form
        setJobData({
          title: '',
          description: '',
          requiredTrade: '',
          location: '',
          budget: '',
          budgetType: 'negotiable',
          urgency: 'normal',
          images: []
        });
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.detail || "Failed to post job",
          className: "bg-red-50 border-red-200 text-red-800"
        });
      }
    } catch (error) {
      console.error('Error posting job:', error);
      toast({
        title: "Error",
        description: "Failed to post job. Please try again.",
        className: "bg-red-50 border-red-200 text-red-800"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-white/10 backdrop-blur-lg border-white/20">
      <CardHeader>
        <CardTitle className="text-white flex items-center space-x-2">
          <span>📝</span>
          <span>Post a New Job</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Job Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-white">Job Title *</Label>
            <Input
              id="title"
              name="title"
              value={jobData.title}
              onChange={handleInputChange}
              placeholder="e.g., Install new electrical outlet in kitchen"
              required
              className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white/50"
            />
          </div>

          {/* Job Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-white">Job Description *</Label>
            <textarea
              id="description"
              name="description"
              value={jobData.description}
              onChange={handleInputChange}
              placeholder="Describe what needs to be done, materials needed, any specific requirements..."
              required
              rows={4}
              className="w-full bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white/50 rounded-md px-3 py-2 resize-none"
            />
          </div>

          {/* Required Trade */}
          <div className="space-y-2">
            <Label htmlFor="requiredTrade" className="text-white">Required Trade/Service *</Label>
            <select
              id="requiredTrade"
              name="requiredTrade"
              value={jobData.requiredTrade}
              onChange={handleInputChange}
              required
              className="w-full bg-white/20 border-white/30 text-white focus:border-white/50 rounded-md px-3 py-2"
            >
              <option value="">Select a trade/service</option>
              {categories.map((category) => (
                <option key={category} value={category} className="text-gray-800">
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Location */}
          <div className="space-y-2">
            <Label htmlFor="location" className="text-white">Location *</Label>
            <Input
              id="location"
              name="location"
              value={jobData.location}
              onChange={handleInputChange}
              placeholder="e.g., Chicago, IL or specific address"
              required
              className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white/50"
            />
          </div>

          {/* Budget and Budget Type */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget" className="text-white">Budget (optional)</Label>
              <Input
                id="budget"
                name="budget"
                type="number"
                value={jobData.budget}
                onChange={handleInputChange}
                placeholder="e.g., 500"
                className="bg-white/20 border-white/30 text-white placeholder:text-white/60 focus:border-white/50"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="budgetType" className="text-white">Budget Type</Label>
              <select
                id="budgetType"
                name="budgetType"
                value={jobData.budgetType}
                onChange={handleInputChange}
                className="w-full bg-white/20 border-white/30 text-white focus:border-white/50 rounded-md px-3 py-2"
              >
                <option value="negotiable" className="text-gray-800">Negotiable</option>
                <option value="fixed" className="text-gray-800">Fixed Price</option>
                <option value="hourly" className="text-gray-800">Hourly Rate</option>
              </select>
            </div>
          </div>

          {/* Urgency */}
          <div className="space-y-2">
            <Label htmlFor="urgency" className="text-white">Urgency</Label>
            <select
              id="urgency"
              name="urgency"
              value={jobData.urgency}
              onChange={handleInputChange}
              className="w-full bg-white/20 border-white/30 text-white focus:border-white/50 rounded-md px-3 py-2"
            >
              <option value="flexible" className="text-gray-800">Flexible</option>
              <option value="normal" className="text-gray-800">Normal</option>
              <option value="urgent" className="text-gray-800">Urgent</option>
            </select>
          </div>

          {/* Image Upload */}
          <div className="space-y-2">
            <Label htmlFor="images" className="text-white">Job Images (optional)</Label>
            <input
              id="images"
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              className="w-full bg-white/20 border-white/30 text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-white/20 file:text-white hover:file:bg-white/30 rounded-md"
            />
            
            {/* Image Previews */}
            {jobData.images.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {jobData.images.map((image, index) => (
                  <div key={index} className="relative">
                    <img 
                      src={image} 
                      alt={`Job image ${index + 1}`}
                      className="w-full h-20 object-cover rounded"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500   text-white"
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Posting Job...</span>
              </div>
            ) : (
              "Post Job"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default JobPosting;